import {
  BenefitCategoryListResponse,
  benefitsService,
  CreateBenefitCategoryRequest,
  FetchBenefitCategoriesRequest,
  ReorderBenefitCategoriesRequest,
  UpdateBenefitCategoryRequest,
} from '@aslanpay/portal-common-lib';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

const useFetchBenefitCategories = (
  companyId: string | undefined,
  params?: FetchBenefitCategoriesRequest,
  enabled = true
) => {
  return useQuery<BenefitCategoryListResponse, Error>({
    queryKey: ['fetchBenefitCategories', { companyId, ...params }],
    queryFn: () => {
      if (!companyId) throw new Error('companyId is required');
      return benefitsService.fetchBenefitCategories(companyId, params);
    },
    enabled: enabled && !!companyId,
  });
};

const useCreateBenefitCategory = (companyId: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: CreateBenefitCategoryRequest) => {
      if (!companyId) throw new Error('companyId is required');
      return benefitsService.createBenefitCategory(companyId, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fetchBenefitCategories', { companyId }] });
    },
  });
};

const useReorderBenefitCategories = (companyId: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (body: ReorderBenefitCategoriesRequest) => {
      if (!companyId) throw new Error('companyId is required');
      return benefitsService.reorderBenefitCategories(companyId, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fetchBenefitCategories'] });
    },
  });
};

const useUpdateBenefitCategory = (companyId: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      categoryId,
      body,
    }: {
      categoryId: string;
      body: UpdateBenefitCategoryRequest;
    }) => {
      if (!companyId) throw new Error('companyId is required');
      return benefitsService.updateBenefitCategory(companyId, categoryId, body);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fetchBenefitCategories'] });
    },
  });
};

const useDeleteBenefitCategory = (companyId: string | undefined) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (categoryId: string) => {
      if (!companyId) throw new Error('companyId is required');
      if (!categoryId) throw new Error('categoryId is required');
      return benefitsService.deleteBenefitCategory(companyId, categoryId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fetchBenefitCategories'] });
      queryClient.invalidateQueries({ queryKey: ['fetchBenefitsGroupedByCategory'] });
    },
  });
};

const useBenefitCategories = {
  useDeleteBenefitCategory,
  useFetchBenefitCategories,
  useCreateBenefitCategory,
  useReorderBenefitCategories,
  useUpdateBenefitCategory,
};

export default useBenefitCategories;
